import type { MessageFlag, StreamEvent } from '@/api/types';
import type { CachePatch } from '@/cache/patches';
import { labelKeywordsOf } from '@/features/labels/keywords';
import { messageIdForUid, messageIdsOf } from './events';

/**
 * The pure half of the live-update client: one event in, `CachePatch[]` and
 * `RealtimeEffect[]` out. No `QueryClient`, no React, no network — which is
 * what lets every rule below be asserted in a unit test.
 *
 * ## Event → patch (ADR 0002 names, docs/plan/06 §1)
 *
 * | Event                | Result |
 * | -------------------- | ------ |
 * | `flags`              | `message-flags` per distinct flag set, + the folder's counters |
 * | `expunge`            | `messages-removed` + the folder's counters |
 * | `folder_counts`      | the folder's counters |
 * | `new_mail`           | **no list patch** — an `arrival` effect plus counters |
 * | `folder_invalidated` | no patch — a `folder-invalidated` effect |
 * | `resync_required`    | no patch — a `resync` effect |
 * | `folder_list`        | no patch — a `folder-list` effect |
 * | `account_status`     | no patch — an `account-status` effect |
 * | `tasks_changed`      | no patch — a `tasks-changed` effect (refetch the account's tasks) |
 * | `labels_changed`     | no patch — a `labels-changed` effect (refetch the account's labels) |
 * | `hello` / `goodbye`  | nothing; the connection owns them |
 *
 * `new_mail` earning no list patch is the deliberate one. Splicing a row into a
 * list the user is scrolled into moves every row under their cursor and under
 * their pointer mid-triage; the arrival is parked and surfaced as the "N new
 * messages" pill instead (doc 06 §5).
 */

/** A change that is **not** a patch, because it drops or refetches cache entries. */
export type RealtimeEffect =
  /**
   * The stream missed events and cannot say which. Every cached list and count
   * for the account is dropped and refetched.
   *
   * Ignoring this is the stale-mail-forever failure the server's ring buffer
   * exists to prevent: the hub tells a subscriber it fell behind precisely so
   * the client does not go on rendering a list it believes is current.
   */
  | { kind: 'resync'; accountId: string; reason?: string }
  /**
   * `UIDVALIDITY` changed. **Every `message_id` held for this folder now names
   * a different message**, so they are discarded rather than refetched under
   * their old keys — a refetch by a stale id would either 404 or, worse,
   * quietly return someone else's message.
   */
  | { kind: 'folder-invalidated'; accountId: string; folderId: string; uidValidity?: number }
  /** The folder tree itself changed. */
  | { kind: 'folder-list'; accountId: string }
  /** New mail parked for the "N new messages" pill; never spliced into a list. */
  | { kind: 'arrival'; accountId: string; folderId: string; messageIds: string[] }
  /** One account's connection state, for the per-account status indicator. */
  | { kind: 'account-status'; accountId: string; status: string; reason?: string }
  /**
   * The account's tasks changed on another device or tab (ADR 0069). The
   * frame names no list or task — the stream says what changed, the REST API
   * says what it now is — so every task read of the account is refetched.
   */
  | { kind: 'tasks-changed'; accountId: string }
  /**
   * The account's labels changed (plan/17 §4): a label was made, renamed,
   * recoloured, reordered or deleted somewhere. Its list is refetched; the
   * messages' keywords arrive as `flags` frames of their own.
   */
  | { kind: 'labels-changed'; accountId: string };

/* --------------------------------------------------------------------- flags */

/**
 * Raw IMAP flag atoms → the app's `MessageFlag` vocabulary.
 *
 * The stream carries what IMAP said (`\Seen`, `$Forwarded`); `types.ts` models
 * the lowercase names the REST surface uses. Mapping here, once, is what keeps
 * a pushed flag change and a locally-made one producing the same patch.
 */
const FLAG_ATOMS: Readonly<Record<string, MessageFlag>> = {
  '\\seen': 'seen',
  '\\flagged': 'flagged',
  '\\answered': 'answered',
  '\\deleted': 'deleted',
  '\\draft': 'draft',
  $forwarded: 'forwarded',
  '\\forwarded': 'forwarded',
};

/** Every flag the app models, so an absent one can be removed explicitly. */
const KNOWN_FLAGS: readonly MessageFlag[] = [
  'seen',
  'flagged',
  'answered',
  'forwarded',
  'draft',
  'deleted',
];

export function normalizeFlags(atoms: readonly string[]): MessageFlag[] {
  const out: MessageFlag[] = [];
  for (const atom of atoms) {
    const flag = FLAG_ATOMS[atom.trim().toLowerCase()];
    if (flag !== undefined && !out.includes(flag)) out.push(flag);
  }
  return out;
}

/**
 * An absolute flag set as the add/remove pair the patch layer speaks.
 *
 * IMAP reports the *whole* set after a change, so "not present" is genuine
 * information: a flag the app models and the server did not list has been
 * cleared, and saying so is what makes a remote "mark unread" land here.
 * Flags outside `KNOWN_FLAGS` (keywords the app does not model) are left alone
 * rather than being stripped by omission.
 */
export function flagUpdateFor(atoms: readonly string[]): { add: MessageFlag[]; remove: MessageFlag[] } {
  const add = normalizeFlags(atoms);
  return { add, remove: KNOWN_FLAGS.filter((flag) => !add.includes(flag)) };
}

/* ------------------------------------------------------------------ reducer */

function counts(event: StreamEvent, accountId: string): CachePatch[] {
  if (event.folderId === undefined) return [];
  if (event.unreadCount === undefined && event.totalCount === undefined) return [];
  return [
    {
      kind: 'folder-counts-set',
      accountId,
      folderId: event.folderId,
      totals: { unread: event.unreadCount ?? 0, total: event.totalCount ?? 0 },
    },
  ];
}

/** Pure: the cache patches one event implies. */
export function patchesForEvent(event: StreamEvent): CachePatch[] {
  const accountId = event.accountId;
  if (accountId === undefined || accountId === '') return [];

  switch (event.kind) {
    case 'flags': {
      const patches: CachePatch[] = [];
      // Grouped by flag set: one patch per distinct outcome, rather than one
      // per message, so a 200-message "mark all read" is a handful of writes.
      // The label keywords ride in the same raw flags (plan/17 §6): the
      // whole set each message has now, so a label added or taken off by
      // another client lands on the row with its flags.
      const groups = new Map<
        string,
        { update: ReturnType<typeof flagUpdateFor>; labelKeywords: string[]; ids: string[] }
      >();
      for (const change of event.flags ?? []) {
        const messageId = messageIdForUid(event, change.uid);
        if (messageId === undefined) continue;
        const update = flagUpdateFor(change.flags);
        const labelKeywords = labelKeywordsOf(change.flags);
        const key = `${update.add.join(',')}|${update.remove.join(',')}|${labelKeywords.join(' ')}`;
        const group = groups.get(key) ?? { update, labelKeywords, ids: [] };
        group.ids.push(messageId);
        groups.set(key, group);
      }
      for (const group of groups.values()) {
        patches.push({
          kind: 'message-flags',
          accountId,
          messageIds: group.ids,
          update: group.update,
          // Absolute sets: the conversation lists sync instead.
          absolute: true,
          labelKeywords: group.labelKeywords,
        });
      }
      return [...patches, ...counts(event, accountId)];
    }

    case 'expunge': {
      const messageIds = messageIdsOf(event);
      return [
        ...(messageIds.length > 0
          ? ([{ kind: 'messages-removed', accountId, messageIds }] as CachePatch[])
          : []),
        ...counts(event, accountId),
      ];
    }

    case 'new_mail':
    case 'folder_counts':
      return counts(event, accountId);

    default:
      return [];
  }
}

/** Pure: the non-patch consequences of one event. */
export function effectsForEvent(event: StreamEvent): RealtimeEffect[] {
  const accountId = event.accountId ?? '';

  switch (event.kind) {
    case 'resync_required':
      // Emitted even with no account id: the server sends it without one when
      // the whole stream fell behind, and "" means "every account".
      return [{ kind: 'resync', accountId, ...(event.reason ? { reason: event.reason } : {}) }];

    case 'folder_invalidated':
      if (accountId === '' || event.folderId === undefined) return [];
      return [
        {
          kind: 'folder-invalidated',
          accountId,
          folderId: event.folderId,
          ...(event.uidValidity === undefined ? {} : { uidValidity: event.uidValidity }),
        },
      ];

    case 'folder_list':
      return accountId === '' ? [] : [{ kind: 'folder-list', accountId }];

    case 'tasks_changed':
      return accountId === '' ? [] : [{ kind: 'tasks-changed', accountId }];

    case 'labels_changed':
      return accountId === '' ? [] : [{ kind: 'labels-changed', accountId }];

    case 'new_mail': {
      if (accountId === '' || event.folderId === undefined) return [];
      const messageIds = messageIdsOf(event);
      if (messageIds.length === 0) return [];
      return [{ kind: 'arrival', accountId, folderId: event.folderId, messageIds }];
    }

    case 'account_status':
      if (accountId === '' || event.status === undefined) return [];
      return [
        {
          kind: 'account-status',
          accountId,
          status: event.status,
          ...(event.reason ? { reason: event.reason } : {}),
        },
      ];

    default:
      return [];
  }
}
